<script lang="ts" setup>
import {computed, defineAsyncComponent, nextTick, ref, watch} from "vue";

import {
    FaceSmileIcon,
    MicrophoneIcon,
    MinusIcon,
    PaperAirplaneIcon,
    PaperClipIcon,
    PlusIcon,
    XCircleIcon
} from "@heroicons/vue/24/outline";
import IconButton from "@src/components/ui/inputs/IconButton.vue";
import {useChatSessionStore} from "@src/stores/chatSession";
import {useMessageStore} from "@src/stores/messages";
const EmojiPicker = defineAsyncComponent(() => import("@src/components/ui/inputs/EmojiPicker/EmojiPicker.vue"));
import ScaleTransition from "@src/components/ui/transitions/ScaleTransition.vue";
import OptionsSelector from "@src/components/views/HomeView/MainArea/FooterArea/OptionsSelector/OptionsSelector.vue";
import AttachSelector from "@src/components/views/HomeView/MainArea/FooterArea/AttachSelector/AttachSelector.vue";
import AudioRecorder from "@src/components/ui/inputs/AudioRecorder.vue";
import {useAudioStore} from "@src/stores/audio";
import {useIsMobile} from "@src/composables/useIsMobile";
import PasteImage from "@src/components/ui/inputs/PasteImage.vue";
import PasteDocument from "@src/components/ui/inputs/PasteDocument.vue";

const props = defineProps<{
    canSend: boolean
}>()

const emit = defineEmits<{
    sendMessage: []
    handleKeydown: [event: KeyboardEvent]
}>()

const sessionStore = useChatSessionStore()
const messageStore = useMessageStore()
const audioStore = useAudioStore()
const { isMobile } = useIsMobile()

const showPicker = ref(false);
const textareaRef = ref<HTMLTextAreaElement | null>(null);

const showPasteImageModal = ref(false);
const showPasteDocumentModal = ref(false);
const pasteFile = ref<DataTransferItem | null>(null);

const MAX_HEIGHT = 80;

const placeHolder = computed(() => {
    if (!props.canSend) {
        let metaTypes = ['instagram', 'facebook']
        messageStore.message = '';
        if (metaTypes.includes(sessionStore.selectedSession?.channel?.type ?? '') && sessionStore.selectedSession?.is_expired) {
            return 'Essa sessão expirou! Finalize ou aguarde o cliente enviar uma nova mensagem.';
        } else {
            return 'Canal desconectado — não é possível enviar mensagens.'
        }
    } else {
        return isMobile.value
            ? "Escreva a sua mensagem..."
            : "Escreva a sua mensagem... Aperte SHIFT+ENTER para pular uma linha";
    }
})


const handleInput = () => {
    const el = textareaRef.value;
    if (!el) return;

    el.style.height = "auto";
    const newHeight = Math.min(el.scrollHeight, MAX_HEIGHT);
    el.style.height = newHeight + "px";
    el.style.overflowY = el.scrollHeight > MAX_HEIGHT ? "auto" : "hidden";
};

const selectEmoji = (emoji: string) => {
    messageStore.message += emoji;
    showPicker.value = false;

    nextTick(() => {
        if (textareaRef.value) {
            textareaRef.value.focus();
            const len = textareaRef.value.value.length;
            textareaRef.value.setSelectionRange(len, len);
        }
    });
}

const handleClickOutside = (event: Event) => {
    const target = event.target as HTMLElement;
    const parent = target.parentElement as HTMLElement;

    if (
        target &&
        !target.classList.contains("toggle-picker-button") &&
        parent &&
        !parent.classList.contains("toggle-picker-button")
    ) {
        showPicker.value = false;
    }
};

const handleClickOutsideOptions = (event: Event) => {
    const target = event.target as HTMLElement;
    if (target.closest('.toggle-options-button') || target.closest('.options-selector-menu')) return;
    sessionStore.openOptionsSelector = false;
};

const handleClickOutsideAttach = (event: Event) => {
    const target = event.target as HTMLElement;
    if (target.closest('.toggle-attach-button') || target.closest('.attach-selector-menu')) return;
    sessionStore.openAttachSelector = false;
};

const onPaste = (event: ClipboardEvent) => {
    let item = event.clipboardData?.items[0];
    if (!item) return;

    if (item.type.indexOf("image") !== -1) {
        pasteFile.value = item
        showPasteImageModal.value = true
    } else if (item.kind === 'file') {
        pasteFile.value = item
        showPasteDocumentModal.value = true
    }
}

defineExpose({textareaRef})

watch(() => messageStore.message, () => {
    nextTick(() => {
        handleInput();
    })
});
</script>

<template>
    <div class="w-full h-full flex flex-row items-end">
        <div class="min-h-[2.75rem] flex flex-row items-center justify-end">
            <!-- more options button -->
            <IconButton
                v-if="!audioStore.isRecording && props.canSend"
                :class="sessionStore.openOptionsSelector ? 'rotate-180' : 'rotate-0'"
                aria-label="open select attachments modal"
                class="ic-btn-ghost-primary toggle-options-button cursor-pointer w-6 h-6 md:mr-5 xs:mr-4 transition-transform duration-300"
                title="open select attachments modal"
                @click="sessionStore.openOptionsSelector = !sessionStore.openOptionsSelector"
            >
                <PlusIcon v-if="!sessionStore.openOptionsSelector" class="transition-opacity duration-300"/>
                <MinusIcon v-else class="transition-opacity duration-300"/>
            </IconButton>
            <IconButton
                v-if="!audioStore.isRecording && props.canSend"
                :class="sessionStore.openAttachSelector ? 'rotate-180' : 'rotate-0'"
                aria-label="open select attachments modal"
                class="ic-btn-ghost-primary toggle-attach-button cursor-pointer w-6 h-6 md:mr-5 xs:mr-4 transition-transform duration-300"
                title="open select attachments modal"
                @click="sessionStore.openAttachSelector = !sessionStore.openAttachSelector"
            >
                <PaperClipIcon v-if="!sessionStore.openAttachSelector"
                               class="w-[1.25rem] h-[1.25rem] transition-opacity duration-300"/>
                <MinusIcon v-else class="transition-opacity duration-300"/>
            </IconButton>
        </div>

        <Transition
            enter-active-class="transition-all duration-300 ease-out"
            enter-from-class="opacity-0 translate-y-2"
            enter-to-class="opacity-100 translate-y-0"
            leave-active-class="transition-all duration-200 ease-in"
            leave-from-class="opacity-100 translate-y-0"
            leave-to-class="opacity-0 translate-y-2"
        >
            <div
                v-if="sessionStore.openOptionsSelector"
                v-click-outside="handleClickOutsideOptions"
                class="options-selector-menu absolute rounded transition-all duration-200 z-90 left-[10px] md:left-[380px] bottom-[70px]"
            >
                <OptionsSelector/>
            </div>
        </Transition>

        <Transition
            enter-active-class="transition-all duration-300 ease-out"
            enter-from-class="opacity-0 translate-y-2"
            enter-to-class="opacity-100 translate-y-0"
            leave-active-class="transition-all duration-200 ease-in"
            leave-from-class="opacity-100 translate-y-0"
            leave-to-class="opacity-0 translate-y-2"
        >
            <div
                v-if="sessionStore.openAttachSelector"
                v-click-outside="handleClickOutsideAttach"
                class="attach-selector-menu absolute rounded transition-all duration-200 z-90 left-[50px] md:left-[420px] bottom-[70px]"
            >
                <AttachSelector/>
            </div>
        </Transition>

        <!-- message textarea -->
        <div v-if="!audioStore.isRecording" class="grow md:mr-5 xs:mr-4 self-end">
            <div
                class="relative flex flex-row items-center gap-2
                 border border-gray-300 dark:border-gray-600
                 rounded-2xl px-4 py-4
                 focus-within:border-indigo-400
                 transition-colors bg-white dark:bg-gray-900"
            >
                <textarea
                    ref="textareaRef"
                    v-model="messageStore.message"
                    :disabled="!props.canSend"
                    :placeholder="placeHolder"
                    class="w-full resize-none bg-transparent outline-none border-0 focus:ring-0 text-sm placeholder:text-gray-400 dark:text-white"
                    rows="1"
                    @input="handleInput"
                    @keydown="emit('handleKeydown', $event)"
                    @paste="onPaste"
                />
            </div>
        </div>

        <div v-if="audioStore.isRecording" class="grow md:mr-5 xs:mr-4 self-end">
            <div class="relative flex flex-row items-center gap-2
                 border border-gray-300 dark:border-gray-600
                 rounded-2xl px-4 py-3
                 bg-white dark:bg-gray-900">
                <AudioRecorder/>
            </div>
        </div>

        <div class="min-h-[2.75rem] flex flex-row items-center justify-end">
            <!-- emoji button -->
            <IconButton
                v-if="!audioStore.isRecording && props.canSend"
                aria-label="toggle emoji picker"
                class="ic-btn-ghost-primary cursor-pointer toggle-picker-button w-7 h-7 md:mr-5 xs:mr-4"
                @click="messageStore.emojiSearch = ''; showPicker = !showPicker"
            >
                <XCircleIcon v-if="showPicker" class="w-[1.25rem] h-[1.25rem]"/>
                <FaceSmileIcon v-else class="w-[1.25rem] h-[1.25rem]"/>
            </IconButton>
            <!-- recording button -->
            <IconButton
                v-if="messageStore.message.length === 0 && !audioStore.isRecording && !audioStore.isStarting && props.canSend"
                aria-label="start recording"
                class="ic-btn-ghost-primary w-7 h-7 cursor-pointer"
                title="start recording"
                @click="audioStore.startRecorder()"
            >
                <MicrophoneIcon class="w-[1.25rem] h-[1.25rem]"/>
            </IconButton>
            <!-- send message button -->
            <IconButton
                v-if="messageStore.message.length > 0"
                aria-label="send message"
                class="ic-btn-contained-primary cursor-pointer w-7 h-7 active:scale-110"
                title="send message"
                @click="emit('sendMessage')"
            >
                <PaperAirplaneIcon class="w-4.25 h-4.25"/>
            </IconButton>
        </div>

        <ScaleTransition>
            <div
                v-show="showPicker"
                v-click-outside="handleClickOutside"
                class="absolute z-10 bottom-13.75 md:right-0 xs:right-[-5rem] mt-2"
            >
                <div role="none">
                    <EmojiPicker v-if="showPicker" :show="showPicker" @select="selectEmoji"/>
                </div>
            </div>
        </ScaleTransition>
        <PasteImage v-if="showPasteImageModal" :file="(pasteFile as any)" @close-modal="showPasteImageModal = false"/>
        <PasteDocument v-if="showPasteDocumentModal" :file="(pasteFile as any)" @close-modal="showPasteDocumentModal = false"/>
    </div>
</template>
